// Browser-side article analysis. Thin wrapper over `articleAnalysisCore.ts`
// that uses `DOMParser` to build the MinimalDoc adapter and forwards to
// analyzeFromDoc. Also exports `enhanceHtmlForPreview` for the dashboard
// preview enhancements (FAQ accordion, takeaways callout, anchor IDs) —
// browser-only since it mutates DOM nodes.

import {
    EMPTY_REPORT,
    analyzeFromDoc,
    dedupeSlug,
    slugify,
    type AnalyzeMeta,
    type ArticleStats,
    type Bucket,
    type ChecklistItem,
    type Heading,
    type MinimalDoc,
    type MinimalElement,
    type ScoreReport,
    type StatThresholds,
} from "./articleAnalysisCore";

export type {
    AnalyzeMeta,
    ArticleStats,
    Bucket,
    ChecklistItem,
    Heading,
    ScoreReport,
    StatThresholds,
};
export { DEFAULT_THRESHOLDS } from "./articleAnalysisCore";
export { EMPTY_REPORT };

function parseHtml(html: string): Document | null {
    if (typeof window === "undefined" || typeof DOMParser === "undefined") {
        return null;
    }
    try {
        return new DOMParser().parseFromString(html, "text/html");
    } catch {
        return null;
    }
}

// Wrap a real DOMParser Document as a MinimalDoc. The browser DOM already
// satisfies the interface — this is just a type bridge so the core lib
// doesn't import lib.dom types.
function wrapDocument(doc: Document): MinimalDoc {
    const wrapElement = (el: Element): MinimalElement => ({
        tagName: el.tagName,
        textContent: el.textContent ?? "",
        getAttribute: (name) => el.getAttribute(name),
    });

    return {
        body: {
            get textContent(): string {
                return doc.body.textContent ?? "";
            },
        },
        querySelector(selector: string): MinimalElement | null {
            const found = doc.querySelector(selector);
            return found ? wrapElement(found) : null;
        },
        querySelectorAll(selector: string): MinimalElement[] {
            return Array.from(doc.querySelectorAll(selector)).map(wrapElement);
        },
    };
}

export function analyzeArticle(html: string, meta: AnalyzeMeta): ScoreReport {
    const doc = parseHtml(html);
    if (!doc) return EMPTY_REPORT;
    return analyzeFromDoc(wrapDocument(doc), meta);
}

// HTML enhancement for preview rendering: walks the parsed document, assigns
// stable id slugs to headings, wraps Key Takeaways sections, and converts FAQ
// H3/content pairs into <details> accordions. Returns the serialized body
// innerHTML ready for dangerouslySetInnerHTML.
//
// This stays browser-only — the server-side scoring gate doesn't need
// preview enhancement.
export function enhanceHtmlForPreview(html: string): string {
    const doc = parseHtml(html);
    if (!doc) return html;

    // 1. Assign id slugs to h2/h3.
    const used = new Set<string>();
    const headings = Array.from(doc.querySelectorAll("h2, h3"));
    for (const h of headings) {
        const text = (h.textContent ?? "").trim();
        const slug = dedupeSlug(slugify(text), used);
        h.setAttribute("id", slug);
    }

    // 2. Wrap Key Takeaways block: find H2 matching the regex; wrap it + the
    //    next sibling (usually a <table> or <ul>) inside an <div.article-kta>.
    const ktaRegex = /key takeaway|takeaways|tl;?dr|summary|resumen/i;
    for (const h of Array.from(doc.querySelectorAll("h2, h3"))) {
        if (!ktaRegex.test(h.textContent ?? "")) continue;
        const next = h.nextElementSibling;
        if (!next || !/^(TABLE|UL|OL|P)$/.test(next.tagName)) continue;
        const wrapper = doc.createElement("div");
        wrapper.className = "article-kta";
        h.parentNode?.insertBefore(wrapper, h);
        wrapper.appendChild(h);
        wrapper.appendChild(next);
        break; // one callout is enough
    }

    // 3. FAQ accordion: find the H2 matching /faq|frequently asked/i, then
    //    pair each following H3 with its following content until the next H2
    //    or end of document. Each pair becomes a <details> element.
    const faqRegex = /faq|frequently asked|preguntas frecuentes/i;
    const faqHeading = Array.from(doc.querySelectorAll("h2")).find((h) =>
        faqRegex.test(h.textContent ?? ""),
    );

    if (faqHeading) {
        const list = doc.createElement("div");
        list.className = "article-faq-list";

        let cursor: Element | null = faqHeading.nextElementSibling;
        while (cursor && cursor.tagName !== "H2") {
            const next: Element | null = cursor.nextElementSibling;
            if (cursor.tagName === "H3") {
                const details = doc.createElement("details");
                details.className = "article-faq-item";
                const summary = doc.createElement("summary");
                summary.textContent = (cursor.textContent ?? "").trim();
                details.appendChild(summary);

                const body = doc.createElement("div");
                body.className = "article-faq-body";

                // Absorb following siblings until next H3 or H2.
                let filler: Element | null = next;
                while (filler && filler.tagName !== "H3" && filler.tagName !== "H2") {
                    const after: Element | null = filler.nextElementSibling;
                    body.appendChild(filler);
                    filler = after;
                }
                details.appendChild(body);
                list.appendChild(details);

                // Remove the original H3 (already represented in summary).
                cursor.remove();
                cursor = filler;
            } else {
                // Content outside an H3 pair — skip, leave in place.
                cursor = next;
            }
        }

        if (list.childElementCount > 0) {
            faqHeading.parentNode?.insertBefore(list, faqHeading.nextSibling);
        }
    }

    return doc.body.innerHTML;
}
